import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Check, CircleAlert, FolderOpen, UploadCloud } from 'lucide-react';
import type { Folder, UploadResult } from '../../shared/types';
import { uploadFiles } from '../lib/api';
import { formatBytes } from '../lib/format';
import { errorMessage, useToast } from './toast';

const ACCEPT = '.zip,.gif,.jpg,.jpeg,.png,.webp,.mp4,.mov,.webm';
const JUNK = /(^|\/)(__MACOSX\/|\.DS_Store$|Thumbs\.db$|desktop\.ini$)/i;

interface QueueItem {
  name: string;
  size: number;
}

interface Selection {
  files: File[];
  /** Relative Pfade bei Ordnern – dann zerlegt der Server die Ordner in einzelne Motive. */
  paths: string[] | null;
}

/** Größe und Dauer von Videos im Browser auslesen – der Server ergänzt damit WebM-Dateien. */
function probeVideo(file: File): Promise<{ width?: number; height?: number; durationMs?: number }> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    const done = (value: { width?: number; height?: number; durationMs?: number }) => {
      URL.revokeObjectURL(url);
      resolve(value);
    };
    const timer = setTimeout(() => done({}), 4000);
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      clearTimeout(timer);
      done({
        width: video.videoWidth || undefined,
        height: video.videoHeight || undefined,
        durationMs: Number.isFinite(video.duration) ? Math.round(video.duration * 1000) : undefined,
      });
    };
    video.onerror = () => {
      clearTimeout(timer);
      done({});
    };
    video.src = url;
  });
}

function readEntries(dir: FileSystemDirectoryEntry): Promise<FileSystemEntry[]> {
  const reader = dir.createReader();
  const all: FileSystemEntry[] = [];
  return new Promise((resolve, reject) => {
    // readEntries liefert in Portionen, bis eine leere kommt.
    const next = () =>
      reader.readEntries((batch) => {
        if (batch.length === 0) resolve(all);
        else {
          all.push(...batch);
          next();
        }
      }, reject);
    next();
  });
}

async function walk(entry: FileSystemEntry, prefix: string, out: { file: File; path: string }[]) {
  if (entry.isFile) {
    const file = await new Promise<File>((resolve, reject) =>
      (entry as FileSystemFileEntry).file(resolve, reject),
    );
    out.push({ file, path: prefix + entry.name });
  } else if (entry.isDirectory) {
    for (const child of await readEntries(entry as FileSystemDirectoryEntry)) {
      await walk(child, `${prefix}${entry.name}/`, out);
    }
  }
}

/** Abgelegte Dateien und Ordner einsammeln. Muss im Drop-Handler aufgerufen werden (synchroner Zugriff). */
function collectDrop(dt: DataTransfer): Promise<Selection> {
  const entries = Array.from(dt.items ?? [])
    .filter((item) => item.kind === 'file')
    .map((item) => item.webkitGetAsEntry?.() ?? null)
    .filter((e): e is FileSystemEntry => e !== null);
  const files = Array.from(dt.files);
  if (!entries.some((e) => e.isDirectory)) return Promise.resolve({ files, paths: null });
  return (async () => {
    const out: { file: File; path: string }[] = [];
    for (const entry of entries) await walk(entry, '', out);
    const kept = out.filter((o) => !JUNK.test(o.path));
    return { files: kept.map((o) => o.file), paths: kept.map((o) => o.path) };
  })();
}

export function UploadZone({
  campaignId,
  bannerId,
  folders,
  big = false,
  pageDrop = false,
  label,
  onDone,
}: {
  campaignId: string;
  bannerId?: string;
  /** Mit Ordnern: Auswahl des Zielordners und Ordner-Upload. */
  folders?: Folder[];
  big?: boolean;
  pageDrop?: boolean;
  label?: string;
  onDone?: (results: UploadResult[]) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const dirInput = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [pageOver, setPageOver] = useState(false);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [results, setResults] = useState<UploadResult[]>([]);
  const [progress, setProgress] = useState<number | null>(null);
  const [folderId, setFolderId] = useState('');
  const queryClient = useQueryClient();
  const toast = useToast();
  const withFolders = folders !== undefined && !bannerId;

  useEffect(() => {
    dirInput.current?.setAttribute('webkitdirectory', '');
  }, []);
  useEffect(() => {
    if (folderId && folders && !folders.some((f) => f.id === folderId)) setFolderId('');
  }, [folders, folderId]);

  const start = async ({ files, paths }: Selection) => {
    if (files.length === 0 || progress !== null) return;
    if (paths && bannerId) {
      toast('Für eine neue Version bitte das ZIP oder die Datei hochladen, keinen Ordner.', 'error');
      return;
    }
    if (paths) {
      // Ordner zusammengefasst anzeigen statt hunderter einzelner Dateien.
      const roots = new Map<string, QueueItem>();
      files.forEach((f, i) => {
        const root = paths[i].split('/')[0];
        const item = roots.get(root) ?? { name: paths[i].includes('/') ? `${root}/` : root, size: 0 };
        item.size += f.size;
        roots.set(root, item);
      });
      setQueue([...roots.values()]);
    } else {
      setQueue(files.map((f) => ({ name: f.name, size: f.size })));
    }
    setResults([]);
    setProgress(0);
    try {
      const hints: Record<string, { width?: number; height?: number; durationMs?: number }> = {};
      let probed = 0;
      for (const [i, file] of files.entries()) {
        if (!file.type.startsWith('video/') || probed >= 50) continue;
        hints[paths ? paths[i] : file.name] = await probeVideo(file);
        probed += 1;
      }
      const list = await uploadFiles(campaignId, files, {
        bannerId,
        folderId: withFolders && folderId ? folderId : undefined,
        hints,
        paths: paths ?? undefined,
        onProgress: setProgress,
      });
      setResults(list);
      setQueue([]);
      const ok = list.filter((r) => r.ok).length;
      const failed = list.length - ok;
      if (ok) {
        const created = list.filter((r) => r.ok && r.isNewBanner).length;
        toast(
          `${ok} Werbemittel hochgeladen${created && created !== ok ? `, davon ${created} neu` : ''}${failed ? `, ${failed} abgelehnt` : ''}.`,
        );
      } else toast('Keine Datei konnte verarbeitet werden.', 'error');
      await queryClient.invalidateQueries({ queryKey: ['campaign', campaignId] });
      if (bannerId) await queryClient.invalidateQueries({ queryKey: ['banner', bannerId] });
      onDone?.(list);
    } catch (err) {
      toast(errorMessage(err), 'error');
      setQueue([]);
    } finally {
      setProgress(null);
      if (input.current) input.current.value = '';
      if (dirInput.current) dirInput.current.value = '';
    }
  };

  const fromInput = (list: FileList | null, withPaths: boolean) => {
    if (!list) return;
    const files = Array.from(list);
    if (!withPaths) return void start({ files, paths: null });
    const kept = files.filter((f) => !JUNK.test(f.webkitRelativePath || f.name));
    void start({ files: kept, paths: kept.map((f) => f.webkitRelativePath || f.name) });
  };

  // Dateien und Ordner überall auf der Seite fallen lassen können.
  useEffect(() => {
    if (!pageDrop) return;
    let depth = 0;
    const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');
    const enter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth += 1;
      setPageOver(true);
    };
    const leave = () => {
      depth = Math.max(0, depth - 1);
      if (depth === 0) setPageOver(false);
    };
    const overHandler = (e: DragEvent) => {
      if (hasFiles(e)) e.preventDefault();
    };
    const drop = (e: DragEvent) => {
      if (!hasFiles(e) || !e.dataTransfer) return;
      e.preventDefault();
      depth = 0;
      setPageOver(false);
      void collectDrop(e.dataTransfer).then(start);
    };
    window.addEventListener('dragenter', enter);
    window.addEventListener('dragleave', leave);
    window.addEventListener('dragover', overHandler);
    window.addEventListener('drop', drop);
    return () => {
      window.removeEventListener('dragenter', enter);
      window.removeEventListener('dragleave', leave);
      window.removeEventListener('dragover', overHandler);
      window.removeEventListener('drop', drop);
    };
  });

  const busy = progress !== null;
  const targetName = folders?.find((f) => f.id === folderId)?.name;
  return (
    <div>
      <div
        className={`dropzone ${big ? 'big' : ''} ${over ? 'is-over' : ''}`}
        role="button"
        tabIndex={0}
        aria-busy={busy}
        onClick={() => !busy && input.current?.click()}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && input.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          if (pageDrop) return; // übernimmt der Seiten-Handler
          e.preventDefault();
          setOver(false);
          void collectDrop(e.dataTransfer).then(start);
        }}
      >
        <div className="dropzone-icon">
          {busy ? <div className="spinner" style={{ borderTopColor: '#fff' }} /> : <UploadCloud size={22} />}
        </div>
        <div className="stack grow" style={{ gap: 2 }}>
          <strong>
            {busy
              ? `Lade hoch … ${Math.round((progress ?? 0) * 100)} %`
              : (label ?? 'Werbemittel hierher ziehen oder auswählen')}
          </strong>
          <span className="small muted">
            {bannerId
              ? 'HTML5 als ZIP, GIF, JPG, PNG, WebP, MP4, MOV oder WebM.'
              : 'HTML5-ZIP, GIF, Bild oder Video – einzeln, mehrere auf einmal, als Paket-ZIP oder ganze Ordner. Unterordner werden zu Ordnern, gleich benannte Dateien zu neuen Versionen.'}
            {targetName && ` Ziel: „${targetName}“.`}
          </span>
        </div>
        {withFolders && (
          <div
            className="dropzone-side"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={busy}
              onClick={() => dirInput.current?.click()}
            >
              <FolderOpen size={15} /> Ordner wählen
            </button>
            {folders.length > 0 && (
              <select
                className="select select-sm"
                value={folderId}
                onChange={(e) => setFolderId(e.target.value)}
                aria-label="Zielordner"
                disabled={busy}
              >
                <option value="">Ordner: automatisch</option>
                {folders.map((f) => (
                  <option key={f.id} value={f.id}>
                    In „{f.name}“
                  </option>
                ))}
              </select>
            )}
          </div>
        )}
        <input
          ref={input}
          type="file"
          multiple={!bannerId}
          accept={ACCEPT}
          hidden
          onChange={(e) => fromInput(e.target.files, false)}
        />
        <input ref={dirInput} type="file" multiple hidden onChange={(e) => fromInput(e.target.files, true)} />
      </div>
      {(queue.length > 0 || results.length > 0) && (
        <div className="upload-list" aria-live="polite">
          {queue.map((item) => (
            <div key={item.name} className="upload-item">
              <span className="grow ellipsis">
                {item.name} <span className="muted">· {formatBytes(item.size)}</span>
              </span>
              <span className="muted">wird verarbeitet …</span>
              {busy && (
                <div className="progress">
                  <span style={{ width: `${Math.round((progress ?? 0) * 100)}%` }} />
                </div>
              )}
            </div>
          ))}
          {results.map((r, i) => (
            <div key={`${r.fileName}-${i}`} className="upload-item">
              <span className="grow ellipsis" title={r.fileName}>
                {r.fileName || 'Upload'}
                {r.folderName && <span className="chip chip-outline upload-folder">{r.folderName}</span>}
              </span>
              {r.ok ? (
                <span className="status-icon status-pass">
                  <Check size={14} strokeWidth={3} />
                  {r.isNewBanner ? 'Neues Motiv' : `Version ${r.versionNumber}`}
                </span>
              ) : (
                <span className="status-icon status-fail" title={r.error}>
                  <CircleAlert size={14} /> {r.error}
                </span>
              )}
            </div>
          ))}
          {results.length > 0 && !busy && (
            <button className="text-link small upload-clear" onClick={() => setResults([])}>
              Liste ausblenden
            </button>
          )}
        </div>
      )}
      {pageOver && <div className="drop-overlay">Loslassen zum Hochladen – Dateien, Pakete oder Ordner</div>}
    </div>
  );
}
